import api from "../helpers/axios";
import { extractUserFromToken } from "../helpers/auth";

class IncidentService {
  /**
   * Get paginated incidents list with role-based filtering
   * @param {Object} filters - Filter options
   * @param {string} filters.status - Incident status (optional)
   * @param {number} filters.facilityId - Facility ID for superadmin (optional)
   * @param {string} filters.assignedUserId - Filter by assigned user, use "me" for current user (optional)
   * @param {number} filters.limit - Items per page (default 20)
   * @param {string} filters.cursor - Pagination cursor (optional)
   * @returns {Promise<Object>} Paginated incidents list
   */
  async getIncidentsList(filters = {}) {
    try {
      const user = extractUserFromToken();
      const params = new URLSearchParams();

      // Add filter parameters
      if (filters.status) params.append("status", filters.status);
      if (filters.assignedUserId)
        params.append("assigned_user_id", filters.assignedUserId);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.cursor) params.append("cursor", filters.cursor);

      // Only superadmins can filter by facility_id
      if (filters.facilityId && user?.role === "superadmin") {
        params.append("facility_id", filters.facilityId);
      }

      const response = await api.get(`/admin/incidents?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching incidents list:", error);
      throw error;
    }
  }

  /**
   * Get current user's assigned incidents
   * @param {Object} filters - Filter options
   * @param {string} filters.status - Filter by status (optional)
   * @param {number} filters.limit - Items per page (default 20)
   * @param {string} filters.cursor - Pagination cursor (optional)
   * @returns {Promise<Object>} User's assigned incidents
   */
  async getMyAssignedIncidents(filters = {}) {
    try {
      const params = new URLSearchParams();

      if (filters.status) params.append("status", filters.status);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.cursor) params.append("cursor", filters.cursor);

      const response = await api.get(
        `/admin/incidents/my-assignments?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching my assigned incidents:", error);
      throw error;
    }
  }

  /**
   * Get incident details by ID
   * @param {string} incidentId - Incident ID
   * @returns {Promise<Object>} Incident details
   */
  async getIncidentDetails(incidentId) {
    try {
      const response = await api.get(`/admin/incidents/${incidentId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching incidents list:", error);
      throw error;
    }
  }

  /**
   * Create new incident (superadmin only)
   * @param {Object} incidentData - Incident information
   * @param {string} incidentData.title - Incident title
   * @param {string} incidentData.description - Incident description (optional)
   * @param {number} incidentData.facility_id - Target facility ID
   * @param {string} incidentData.priority - Priority level ("low"|"medium"|"high"|"critical")
   * @param {string} incidentData.assignment_type - Assignment type ("facility_admin"|"direct_assignment")
   * @param {string} incidentData.assigned_user_id - User ID for direct assignment (optional)
   * @param {string[]} incidentData.feedback_ids - Array of feedback IDs to link (optional)
   * @returns {Promise<Object>} Created incident
   */
  async createIncident(incidentData) {
    try {
      const response = await api.post("/admin/incidents", incidentData);
      return response.data;
    } catch (error) {
      console.error("Error creating incident:", error);
      throw error;
    }
  }

  /**
   * Update incident details
   * @param {string} incidentId - Incident ID
   * @param {Object} updateData - Fields to update
   * @param {string} updateData.status - New status (optional)
   * @param {string} updateData.priority - New priority (optional)
   * @param {string} updateData.resolution_notes - Resolution notes (optional)
   * @returns {Promise<Object>} Updated incident
   */
  async updateIncident(incidentId, updateData) {
    try {
      const response = await api.patch(
        `/admin/incidents/${incidentId}`,
        updateData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating incident:", error);
      throw error;
    }
  }

  /**
   * Reassign incident to different user
   * @param {string} incidentId - Incident ID
   * @param {Object} reassignData - Reassignment information
   * @param {string} reassignData.assigned_user_id - New assignee user ID
   * @param {string} reassignData.assignment_note - Note for reassignment
   * @returns {Promise<Object>} Updated incident
   */
  async reassignIncident(incidentId, reassignData) {
    try {
      const response = await api.patch(
        `/admin/incidents/${incidentId}/reassign`,
        reassignData
      );
      return response.data;
    } catch (error) {
      console.error("Error reassigning incident:", error);
      throw error;
    }
  }

  /**
   * Get facility users for assignment dropdown (superadmin only)
   * @param {number} facilityId - Facility ID
   * @returns {Promise<Object>} Facility users list
   */
  async getFacilityUsers(facilityId) {
    try {
      const response = await api.get(
        `/admin/incidents/facility/${facilityId}/users`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching facility users:", error);
      throw error;
    }
  }

  /**
   * Quick status update for incident
   * @param {string} incidentId - Incident ID
   * @param {Object} statusData - Status update
   * @param {string} statusData.status - New status
   * @param {string} statusData.notes - Status change notes (optional)
   * @returns {Promise<Object>} Updated incident
   */
  async updateIncidentStatus(incidentId, statusData) {
    try {
      const response = await api.patch(
        `/admin/incidents/${incidentId}/status`,
        statusData
      );
      return response.data;
    } catch (error) {
      console.error("Error updating incident status:", error);
      throw error;
    }
  }

  async getIncidentLinkedFeedback(incidentId) {
  try {
    const response = await api.get(`/admin/incidents/${incidentId}/feedback`);
      return response.data;
    } catch (error) {
      console.error("Error fetching incident linked feedback:", error);
    throw error;
    }
  }
  
}

const incidentService = new IncidentService();
export default incidentService;
